import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GlobalState } from "context/GlobalContext";
import axios from "axios";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import EditFactoryDetails from "../FactoryDetails/EditFactoryDetails/EditFactoryDetails";
import CopyFactoryModal from "./CopyFactoryModal/CopyFactoryModal";
import {
	EditCompanyDetailsModalTypes,
	factoryUrlParamName,
	foreignKeys,
	formatDateDisplay,
	formatTypeOptionValue,
	supplierDrawerElements,
	supplierUrlParamName,
} from "data/supplierPageData";
import parseErrMsgFromError from "utils/errorFunctions/parseErrorMessageFromError";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SupplierPageHeader, supplierUi } from "../../shared/supplierUi";
import TanstackDatagrid from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";

const factoryTablecolumns = [
	{
		key: "factory_code",
		name: "Factory Code",
		align: "center",
		headerAlign: "center",
		width: 140,
	},
	{
		key: "name",
		name: "Name",
		align: "center",
		headerAlign: "center",
		width: 180,
		flex: 1,
		minWidth: 160,
	},
	{
		key: "factory_type",
		name: "Factory Type",
		align: "center",
		headerAlign: "center",
		width: 140,
		valueGetter: ({ row }) => formatTypeOptionValue(row.factory_type),
		renderCell: ({ row }) => formatTypeOptionValue(row.factory_type) || "—",
	},
	{
		key: "factory_liscence_no",
		name: "Licence No",
		align: "center",
		headerAlign: "center",
		width: 160,
	},
	{
		key: "factory_liscence_expiry_date",
		name: "Licence Expiry",
		align: "center",
		headerAlign: "center",
		width: 140,
		renderCell: ({ row }) => formatDateDisplay(row.factory_liscence_expiry_date),
		valueGetter: ({ row }) => formatDateDisplay(row.factory_liscence_expiry_date, "DD-MM-YYYY", ""),
	},
	{ key: "factory_city", headerAlign: "center", align: "center", name: "City", width: 130 },
	{ key: "factory_state", headerAlign: "center", align: "center", name: "State", width: 130 },
	{
		key: "factory_gst_no",
		headerAlign: "center",
		align: "center",
		name: "GST No",
		width: 140,
	},
	{
		key: "factory_pincode",
		headerAlign: "center",
		align: "center",
		name: "Pincode",
		width: 110,
	},
];

const filterRows = (rows, filters) => {
	let next = rows || [];
	Object.entries(filters || {}).forEach(([key, value]) => {
		if (value == null || String(value).trim() === "") return;
		const q = String(value).toLowerCase();
		const col = factoryTablecolumns.find((c) => c.key === key);
		next = next.filter((row) => {
			const raw = row?.[key];
			const display =
				typeof col?.valueGetter === "function"
					? String(col.valueGetter({ row, value: raw, column: col, field: key }) ?? "")
					: String(raw ?? "");
			return display.toLowerCase().includes(q);
		});
	});
	return next;
};

const hasGinesysFactoryId = (row) => {
	const value = row?.ginesys_factory_id;
	return value != null && String(value).trim() !== "";
};

const AllFactories = ({
	factories,
	setFactories,
	selectedFactory,
	setSelectedFactory,
	companyData,
	setTab,
	disableEdit,
	hidePageHeader = false,
}) => {
	const [modalOpen, setModalOpen] = useState(false);
	const [copyModalOpen, setCopyModalOpen] = useState(false);
	const [copySource, setCopySource] = useState(null);
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [pageData, setPageData] = useState([]);
	const [rowCount, setRowCount] = useState(0);
	const [filterObj, setFilterObj] = useState({});
	const [gridKey, setGridKey] = useState(0);
	const sourceRef = useRef(factories || []);
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();

	useEffect(() => {
		sourceRef.current = factories || [];
		setGridKey((k) => k + 1);
	}, [factories]);

	const getServerSideData = useCallback(async (limit, offset, filters) => {
		const filtered = filterRows(sourceRef.current, filters);
		return {
			rows: filtered.slice(offset, offset + limit),
			total_rows_count: filtered.length,
		};
	}, []);

	const goToFactoryTab = (factoryId) => {
		const params = new URLSearchParams(searchParams);
		params.set(factoryUrlParamName, factoryId);
		params.delete(supplierUrlParamName);
		const qs = params.toString();
		navigate(`../${supplierDrawerElements.factory.key}${qs ? `?${qs}` : ""}`);
	};

	const handleCopyClick = useCallback(async (row) => {
		setLoadingOpen(true);
		try {
			const factory = await axios.get(`/api/supplier/factory?id=${row.id}`);
			setCopySource(factory.data.result || row);
			setCopyModalOpen(true);
		} catch (error) {
			setCopySource(row);
			setCopyModalOpen(true);
		} finally {
			setLoadingOpen(false);
		}
	}, [setLoadingOpen]);

	const handleDeleteClick = useCallback((row) => {
		if (hasGinesysFactoryId(row)) return;
		setDeleteTarget(row);
	}, []);

	const handleDeleteFactory = async () => {
		if (!deleteTarget?.id || hasGinesysFactoryId(deleteTarget)) return;
		setLoadingOpen(true);
		try {
			await axios.delete(`/api/supplier/delete_factory/${deleteTarget.id}`);
			const remaining = (factories || []).filter((factory) => factory.id !== deleteTarget.id);
			setFactories(remaining);
			if (selectedFactory?.id === deleteTarget.id) {
				setSelectedFactory();
			}
			setDeleteTarget(null);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Factory deleted",
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrMsgFromError(error) || "Unable to delete factory",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setLoadingOpen(false);
		}
	};

	const factoryColumns = useMemo(
		() => [
			...factoryTablecolumns,
			{
				key: "actions",
				name: "Actions",
				align: "center",
				headerAlign: "center",
				width: 110,
				minWidth: 110,
				flex: 0,
				valueGetter: () => "",
				renderCell: ({ row }) => {
					const linkedToGinesys = hasGinesysFactoryId(row);
					return (
						<Box display="flex" justifyContent="center" onClick={(e) => e.stopPropagation()}>
							<Tooltip title="Copy factory">
								<IconButton
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										handleCopyClick(row);
									}}
									sx={{ color: supplierUi.textSecondary }}>
									<ContentCopyRoundedIcon fontSize="small" />
								</IconButton>
							</Tooltip>
							<Tooltip
								title={
									linkedToGinesys
										? "Cannot delete a factory linked to Ginesys"
										: "Delete factory"
								}>
								<span>
									<IconButton
										size="small"
										disabled={linkedToGinesys}
										onClick={(e) => {
											e.stopPropagation();
											handleDeleteClick(row);
										}}
										sx={{ color: linkedToGinesys ? supplierUi.textMuted : "#B42318" }}>
										<DeleteRoundedIcon fontSize="small" />
									</IconButton>
								</span>
							</Tooltip>
						</Box>
					);
				},
			},
		],
		[handleCopyClick, handleDeleteClick]
	);

	const handleGridRowClick = async ({ row }) => {
		setLoadingOpen(true);
		try {
			const factory = await axios.get(`/api/supplier/factory?id=${row.id}`);
			setSelectedFactory({ ...factory.data.result });
			goToFactoryTab(row.id);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Unable To Get factory details",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setLoadingOpen(false);
		}
	};

	const createFactory = async (body, setModalOpenFn) => {
		const obj = {};
		obj[foreignKeys.supplier] = companyData.id;
		setLoadingOpen(true);

		try {
			const payload = { ...body, ...obj };
			delete payload.id;
			delete payload.factory_code;
			delete payload.is_same_as_company;
			delete payload.ginesys_factory_id;
			delete payload.factoryFiles;
			delete payload.factoryContactPersons;
			delete payload.factoryComplianceCertificates;
			delete payload.factoryApproval;
			let res = await axios.post(`/api/supplier/create_factory`, payload);
			setSelectedFactory(res.data.result);
			setFactories([...(factories || []), res.data.result]);
			goToFactoryTab(res.data.result.id);
			setLoadingOpen(false);
			setModalOpenFn(false);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Created",
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} catch (error) {
			setLoadingOpen(false);

			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrMsgFromError(error) || "Unable to create factory",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		}
	};

	// Internal: fixed viewport calc (shell header sits above).
	// External: fill flex parent from SupplierUser shell.
	const sectionSx = hidePageHeader
		? { height: "calc(100vh - 200px)", minHeight: 420 }
		: { flex: 1, minHeight: 0, height: 0 };

	const newFactoryBtn = !disableEdit ? (
		<Button
			variant="contained"
			size="small"
			startIcon={<AddRoundedIcon />}
			onClick={() => setModalOpen(true)}
			sx={{
				textTransform: "none",
				fontWeight: 600,
				borderRadius: 1.5,
				px: 1.75,
				boxShadow: "none",
				"&:hover": { boxShadow: "none" },
			}}>
			New Factory
		</Button>
	) : null;

	return (
		<Stack width="100%" spacing={2} sx={sectionSx}>
			{!hidePageHeader ? (
				<SupplierPageHeader
					title="Factories"
					subtitle="Select a factory to view details, documents, and contacts"
					sx={{ mb: 0, flexShrink: 0 }}
					actions={newFactoryBtn}
				/>
			) : null}
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					height: 0,
					width: "100%",
					border: `1px solid ${supplierUi.border}`,
					borderRadius: supplierUi.radius,
					bgcolor: "#fff",
					overflow: "hidden",
					display: "flex",
					flexDirection: "column",
					"& > .MuiPaper-root": {
						flex: 1,
						minHeight: 0,
						height: "100% !important",
					},
				}}>
				<TanstackDatagrid
					key={gridKey}
					columns={factoryColumns}
					pageData={pageData}
					setPageData={setPageData}
					rowCount={rowCount}
					setRowCount={setRowCount}
					filterObj={filterObj}
					setFilterObj={setFilterObj}
					getServerSideData={getServerSideData}
					rowsPerPage={10}
					emptyMessage="No factories found"
					handleRowClick={handleGridRowClick}
					getRowId={(row) => row.id}
					headerRowStyle={{ background: "#FAFAFA" }}
					rowHeight={48}
					headerHeight={48}>
					{hidePageHeader ? <Box display="flex" justifyContent="flex-end">{newFactoryBtn}</Box> : null}
				</TanstackDatagrid>
			</Box>

			<EditFactoryDetails
				modalOpen={modalOpen}
				setModalOpen={setModalOpen}
				basicDetails={selectedFactory}
				setBasicDetails={setSelectedFactory}
				type={EditCompanyDetailsModalTypes.onbarding}
				submitFunction={createFactory}
			/>
			<CopyFactoryModal
				modalOpen={copyModalOpen}
				setModalOpen={setCopyModalOpen}
				sourceFactory={copySource}
				submitFunction={createFactory}
			/>
			<Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
				<DialogTitle>Delete factory</DialogTitle>
				<DialogContent>
					{`Delete ${deleteTarget?.factory_code || deleteTarget?.name || "this factory"}? This cannot be undone.`}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
					<Button variant="contained" color="error" onClick={handleDeleteFactory}>
						Delete
					</Button>
				</DialogActions>
			</Dialog>
		</Stack>
	);
};

export default AllFactories;
